import { Alert, Flex, List, ScreenReaderContent, Text } from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import DataGrid from "react-data-grid";
import Skeleton from "react-loading-skeleton";
import { sortOutcomes } from "../../../../components/LearningOutcomes/utils";
import { MasteryLevelContext } from "../../../../utils/Context";
import { TruncateToolTip } from "../../../../utils/TruncateTooltip";

const AssignmentRubric = ({ assignmentData, assessment }) => {
  const { masteryLevels, ratingScales } = useContext(MasteryLevelContext);
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [loadingGrid, setLoadingGrid] = useState(true);
  const [invalid, setInvalid] = useState(false);

  const renderContent = ({ row, column }) => {
    return (
      <div
        style={{
          lineHeight: 1,
          padding: "10px 2px",
          display: "block",
          position: "relative",
          textWrap: "wrap",
          textAlign: row.id === "raterRow" ? "center" : "left",
          height: "100%",
          fontWeight: row.id === "raterRow" && "bold",
        }}
      >
        <TruncateToolTip
          renderTip={
            <div style={{ maxWidth: 400 }}>{row.ratingLevels[column.key]}</div>
          }
          maxLines={9}
        >
          <Text size="small">{row.ratingLevels[column.key]}</Text>
        </TruncateToolTip>
      </div>
    );
  };

  const renderSLOContent = ({ row, column }) => {
    return (
      <div
        style={{
          lineHeight: 1,
          padding: "10px 2px",
          textWrap: "wrap",
          height: "100%",
        }}
      >
        <TruncateToolTip
          renderTip={<div style={{ width: 250 }}>{row[column.key]}</div>}
          maxLines={9}
        >
          <Text size="small">{row[column.key]}</Text>
        </TruncateToolTip>
      </div>
    );
  };

  const calculateColumnSpan = (level, ratingToMasteryLevelMap) => {
    let count = 0;
    ratingToMasteryLevelMap.levels.forEach((lvl) => {
      if (lvl.mastery_level_id === level.mastery_level_id) {
        count++;
      }
    });
    return count;
  };

  const fillAssignmentRubricDescription = (outcome, level) => {
    let rating = {};
    let scale = assessment.assignment_rubric.rating_scales.find(
      (s) => outcome.id === s.criteria_id
    );
    scale.rating_levels.forEach((lvl) => {
      if (
        lvl.insights_rating_level &&
        (lvl.insights_rating_level === level.id ||
          lvl.insights_rating_level.id === level.id)
      ) {
        rating = lvl;
      }
    });
    return rating ? rating.description : t("ratingScale.outcomeCorrupt");
  };

  useEffect(() => {
    if (assessment && ratingScales.length > 0 && masteryLevels.length > 0) {
      let masteryMapping = null;
      if (assessment.assignment_rubric) {
        // Fetch mastery rating scale from assignment rubric, if it exists
        masteryMapping =
          assessment.assignment_rubric.rating_scales[0]
            .rating_to_mastery_level_map;
      } else {
        const outcomeList = Object.keys(assignmentData).filter((key) =>
          key.startsWith("outcome")
        );
        let outcome = null;
        for (let o of outcomeList) {
          if (assignmentData[o].outcome) {
            outcome = assignmentData[o].outcome;
            break;
          }
        }
        // unplanned assignment rating scale information is returned in the .rating_scale field (for now)
        // planned assignment rating scale information is interpolated from the assignment record in our
        // system and the relevant entry in the .rating_scales field
        if (outcome && outcome.rating_scale) {
          masteryMapping = outcome.rating_scale.rating_to_mastery_level_map;
        } else if (
          outcome &&
          outcome.rating_scales &&
          outcome.rating_scales.length > 0 &&
          assignmentData.assignment.lo_rating_scale_maps
        ) {
          const rating_scale_map =
            assignmentData.assignment.lo_rating_scale_maps.find(
              (map) => map.outcome_id === outcome.id
            );
          const rating_scale = outcome.rating_scales.find(
            (scale) => scale.ddl_id === rating_scale_map.rating_scale_id
          );
          masteryMapping = rating_scale.rating_to_mastery_level_map;
        }
      }

      if (masteryMapping) {
        setInvalid(false);

        const outcomes = Object.values(sortOutcomesInOrder(assignmentData));

        let mapObj = [];
        let rowObj = [{ id: "raterRow", ratingLevels: {} }];
        outcomes.forEach((outcome) => {
          rowObj.push({
            slo: outcome.outcome.name + ": " + outcome.outcome.statement,
            id: outcome.outcome.id,
            ratingLevels: {},
          });
        });

        const map = ratingScales.find((rs) => rs.id === masteryMapping);

        map.levels.forEach((lev) => {
          let ml = masteryLevels.find(
            (level) => level.id === lev.mastery_level_id
          );
          let obj = {
            id: lev.mastery_level_id,
            insights_rating_level: lev.id,
            name: (
              <div
                style={{
                  textAlign: "center",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                }}
              >
                <TruncateToolTip
                  renderTip={<div style={{ maxWidth: 400 }}>{ml.level}</div>}
                  maxLines={1}
                >
                  <Text size="x-small">{ml.level}</Text>
                </TruncateToolTip>
              </div>
            ),
            colSpan(args) {
              if (args.type === "HEADER") {
                return calculateColumnSpan(lev, map);
              }
            },
            renderCell: renderContent,
          };
          obj["key"] = lev.id;
          obj["score"] = parseInt(lev.points);
          mapObj.push(obj);
          rowObj.forEach((row, index) => {
            if (index === 0) {
              row.ratingLevels[lev.id] = lev.points;
            } else {
              const outcome = outcomes.find(
                (o) => o.outcome.id === row.id
              ).outcome;
              if (
                assessment.assignment_rubric &&
                assessment.assignment_rubric.rating_scales.find(
                  (rs) => rs.criteria_id === row.id
                )
              ) {
                // Case 1: Fill descriptions from the assignment rubric
                row.ratingLevels[lev.id] = fillAssignmentRubricDescription(
                  outcome,
                  lev
                );
              } else if (
                outcome.rating_scales &&
                outcome.rating_scales.length !== 0 &&
                outcome.rating_scales.find(
                  (s) => s.rating_to_mastery_level_map === masteryMapping
                )
              ) {
                // Case 2: Fill descriptions from outcome rating scales
                let rating = {};
                let scale = outcome.rating_scales.find(
                  (s) => s.rating_to_mastery_level_map === masteryMapping
                );

                scale.rating_levels.forEach((level) => {
                  if (
                    level.insights_rating_level &&
                    (level.insights_rating_level === lev.id ||
                      level.insights_rating_level.id === lev.id)
                  ) {
                    rating = level;
                  }
                });
                row.ratingLevels[lev.id] = rating
                  ? rating.description
                  : t("ratingScale.outcomeCorrupt");
              } else {
                row.ratingLevels[lev.id] = "";
              }
            }
          });
        });
        mapObj.sort((a, b) => b.score - a.score);
        mapObj.unshift({
          key: "slo",
          name: (
            <ScreenReaderContent>
              {t("ratingScale.sloColumnScreenReader")}
            </ScreenReaderContent>
          ),
          renderCell: renderSLOContent,
        });
        setCols(mapObj);
        setRows(rowObj);
        setLoadingGrid(false);
      } else {
        setInvalid(true);
        setRows([]);
        setCols([]);
      }
    }
  }, [assignmentData, assessment, masteryLevels, ratingScales]);

  const rowHeight = (row) => {
    if (row.id === "raterRow") {
      return 35;
    } else {
      return 150;
    }
  };

  const sortOutcomesInOrder = (data) => {
    let entries = Object.entries(data).filter((key) =>
      key[0].startsWith("outcome")
    );
    entries = sortOutcomes(entries, (lo) => lo[1].outcome);
    const sortedOutcomes = Object.fromEntries(entries);
    return sortedOutcomes;
  };

  return !loadingGrid ? (
    <div>
      <List isUnstyled>
        {Object.keys(sortOutcomesInOrder(assignmentData)).map(
          (assignmentKey, key) => (
            <List.Item key={key} margin="small 0">
              <TruncateToolTip
                maxLines={3}
                renderTip={
                  <div style={{ maxWidth: 400 }}>
                    {assignmentData[assignmentKey].outcome.name}
                  </div>
                }
              >
                <Text as="p" size="small">
                  {assignmentData[assignmentKey].outcome.name}
                </Text>
              </TruncateToolTip>
              <Flex justifyItems="space-around" alignItems="start" wrap="wrap">
                <Flex.Item textAlign="center">
                  <Text size="small">
                    {t("dashboard.avgAssignmentGrade")}:{" "}
                    <Text weight="bold" size="small">
                      {Math.round(assignmentData[assignmentKey].score * 10) /
                        10}
                      %
                    </Text>
                  </Text>
                </Flex.Item>
                <Flex.Item textAlign="center">
                  <Text size="small">
                    {t("dashboard.percentAchievedMastery")}:{" "}
                    <Text weight="bold" size="small">
                      {Math.round(assignmentData[assignmentKey].mastered * 10) /
                        10}
                      %
                    </Text>
                  </Text>
                </Flex.Item>
              </Flex>
            </List.Item>
          )
        )}
      </List>
      {!invalid ? (
        <DataGrid
          className="rdg-light"
          style={{ height: "auto" }}
          columns={cols}
          rows={rows}
          rowHeight={rowHeight}
          headerRowHeight={40}
          defaultColumnOptions={{ width: "minmax(80px, auto)" }}
        />
      ) : (
        <Alert variant="info">{t("dashboard.rubricDetailsNa")}</Alert>
      )}
    </div>
  ) : (
    <Skeleton height={250} />
  );
};

export default AssignmentRubric;
